import { useEffect, useState } from "react";
import { authFetchJson } from "@/lib/api";
import { formatDuration } from "./telemetry/format";
import { CLASS_LABEL, KIND_LABEL, type TelemetryUserDetail } from "./telemetry/types";

type UserDetail = {
  user_id: string;
  recordings_total: number;
  recordings_failed: number;
  recordings_by_source: Record<string, number>;
  totals_by_event: Record<string, number>;
  surface_split: Record<string, number>;
  first_seen: string | null;
  last_seen: string | null;
  recent_events: {
    event: string;
    surface: string | null;
    created_at: string;
    occurred_at?: string;
  }[];
};

type Props = {
  user: { id: string; email: string; full_name?: string | null } | null;
  onClose: () => void;
};

export function AdminUserDrawer({ user, onClose }: Props) {
  const [data, setData] = useState<UserDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [telemetry, setTelemetry] = useState<TelemetryUserDetail | null>(null);
  const userId = user?.id;

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    setLoading(true);
    setData(null);
    setTelemetry(null);
    setError("");
    authFetchJson<UserDetail>(`/api/admin/analytics/users/${userId}`)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Gebruikersgegevens laden mislukt.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    // Best-effort and independent: a user without client telemetry must not
    // make the analytics detail look broken.
    authFetchJson<TelemetryUserDetail>(`/api/admin/telemetry/users/${userId}?days=30`)
      .then((d) => { if (!cancelled) setTelemetry(d); })
      .catch(() => { if (!cancelled) setTelemetry(null); });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  if (!user) return null;

  const surfaceTotal = data
    ? Object.values(data.surface_split).reduce((a, b) => a + b, 0)
    : 0;

  return (
    <>
      <button
        type="button"
        aria-label="Sluit"
        onClick={onClose}
        className="fixed inset-0 z-40 bg-[rgba(10,52,96,0.30)] backdrop-blur-sm"
      />
      <aside
        className="fixed inset-y-0 right-0 z-50 w-full max-w-[480px] overflow-y-auto bg-white shadow-[0_24px_48px_rgba(10,52,96,0.14)]"
        role="dialog"
        aria-label={`Analytics voor ${user.email}`}
      >
        <header className="flex items-start justify-between gap-3 border-b border-[rgba(10,52,96,0.10)] p-5">
          <div className="flex gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[rgba(225,29,72,0.10)] text-sm font-semibold text-[var(--cs-rose)]">
              {(user.full_name ?? user.email).charAt(0).toUpperCase()}
            </span>
            <div>
              <p className="text-sm font-semibold text-[var(--cs-navy)]">
                {user.full_name ?? user.email}
              </p>
              <p className="text-xs text-[var(--cs-ink-muted)]">{user.email}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Sluit"
            className="text-[rgba(10,52,96,0.55)] hover:text-[var(--cs-navy)]"
          >
            ×
          </button>
        </header>

        <div className="space-y-5 p-5">
          {error ? (
            <div
              role="alert"
              className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700"
            >
              {error}
            </div>
          ) : null}

          <section>
            <p className="cs-caption mb-2">Aanmeldingen</p>
            <div className="grid grid-cols-3 gap-2">
              <StatTile
                label="totaal"
                value={data ? (data.totals_by_event.login_success ?? 0) : null}
                loading={loading}
              />
              <StatTile
                label="fail"
                value={data ? (data.totals_by_event.login_failure ?? 0) : null}
                loading={loading}
              />
              <StatTile
                label="resets"
                value={data ? (data.totals_by_event.password_reset_completed ?? 0) : null}
                loading={loading}
              />
            </div>
          </section>

          <section>
            <p className="cs-caption mb-2">Opnames</p>
            <div className="grid grid-cols-4 gap-2">
              <StatTile
                label="totaal"
                value={data ? data.recordings_total : null}
                loading={loading}
              />
              <StatTile
                label="dictaten"
                value={data ? (data.recordings_by_source.live ?? 0) : null}
                loading={loading}
              />
              <StatTile
                label="gesprekken"
                value={data ? (data.recordings_by_source.ambient ?? 0) : null}
                loading={loading}
              />
              <StatTile
                label="mislukt"
                value={data ? data.recordings_failed : null}
                loading={loading}
              />
            </div>
          </section>

          <section>
            <p className="cs-caption mb-2">Platform</p>
            <ul className="space-y-2">
              {(["mobile", "web", "widget", "unknown"] as const).map((k) => {
                const v = data?.surface_split[k] ?? 0;
                const pct = surfaceTotal ? Math.round((v / surfaceTotal) * 100) : 0;
                return (
                  <li key={k}>
                    <div className="mb-1 flex justify-between text-xs text-[var(--cs-ink-muted)]">
                      <span className="capitalize">{k}</span>
                      <span>{pct}%</span>
                    </div>
                    <div className="h-1.5 overflow-hidden rounded-full bg-[var(--cs-ivory)]">
                      <div
                        className="h-full rounded-full bg-[var(--cs-rose)]"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>

          <section>
            <p className="cs-caption mb-2">Telemetrie (30 dagen)</p>
            {telemetry === null ? (
              <p className="text-xs text-[var(--cs-ink-muted)]">Geen client-telemetrie.</p>
            ) : (
              <div className="space-y-3 text-xs">
                <div className="grid grid-cols-3 gap-2">
                  <StatTile label="sessies" value={telemetry.sessions.length} loading={false} />
                  <StatTile label="fouten" value={telemetry.errors.total} loading={false} />
                  <StatTile label="uploads mislukt" value={telemetry.uploads.failed} loading={false} />
                </div>
                {telemetry.screen_time.length > 0 ? (
                  <ul className="space-y-1">
                    {telemetry.screen_time.slice(0, 8).map((r) => (
                      <li key={`${r.surface}-${r.screen}`} className="flex justify-between border-b border-[rgba(10,52,96,0.06)] py-1">
                        <span className="text-[var(--cs-navy)]">{r.surface} · {r.screen}</span>
                        <span className="tabular-nums text-[var(--cs-ink-muted)]">{r.views}× · {formatDuration(r.median_ms)}</span>
                      </li>
                    ))}
                  </ul>
                ) : null}
                {telemetry.recent_errors.slice(0, 10).map((e, i) => (
                  <div key={`e-${String(i)}`} className="border-b border-[rgba(10,52,96,0.06)] py-1">
                    <div className="flex justify-between">
                      <span className="text-[var(--cs-rose)]">{CLASS_LABEL[e.error_class ?? "other"]} · {e.source ?? "app"}</span>
                      <span className="tabular-nums text-[var(--cs-ink-muted)]">{new Date(e.created_at).toLocaleString("nl-BE")}</span>
                    </div>
                    {e.detail ? <div className="truncate text-[var(--cs-ink-muted)]">{e.detail}</div> : null}
                  </div>
                ))}
                {telemetry.recent_uploads.slice(0, 10).map((u, i) => (
                  <div key={`u-${String(i)}`} className="flex justify-between border-b border-[rgba(10,52,96,0.06)] py-1">
                    <span className="text-[var(--cs-navy)]">
                      {KIND_LABEL[u.kind] ?? u.kind}{u.error_class ? ` · ${CLASS_LABEL[u.error_class] ?? u.error_class}` : ""}{u.attempt ? ` · poging ${String(u.attempt)}` : ""}
                    </span>
                    <span className="tabular-nums text-[var(--cs-ink-muted)]">{new Date(u.created_at).toLocaleString("nl-BE")}</span>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section>
            <p className="cs-caption mb-2">Recente gebeurtenissen</p>
            <ul className="space-y-1 text-xs">
              {(data?.recent_events ?? []).map((e, i) => (
                <li
                  key={i}
                  className="flex justify-between gap-2 border-b border-[rgba(10,52,96,0.06)] py-1"
                >
                  <span className="text-[var(--cs-navy)]">{e.event}</span>
                  <span className="text-[var(--cs-ink-muted)]">{e.surface ?? "—"}</span>
                  <span className="tabular-nums text-[var(--cs-ink-muted)]">
                    {new Date(e.occurred_at ?? e.created_at).toLocaleString("nl-BE")}
                  </span>
                </li>
              ))}
              {!loading && !error && (data?.recent_events ?? []).length === 0 ? (
                <li className="py-4 text-center text-[var(--cs-ink-muted)]">
                  Geen gebeurtenissen
                </li>
              ) : null}
            </ul>
          </section>
        </div>
      </aside>
    </>
  );
}

function StatTile({
  label,
  value,
  loading,
}: {
  label: string;
  value: number | null;
  loading: boolean;
}) {
  return (
    <div className="rounded-xl border border-[rgba(10,52,96,0.10)] p-2 text-center">
      <p className="text-xl font-semibold tabular-nums text-[var(--cs-navy)]">
        {loading ? "…" : (value ?? "—")}
      </p>
      <p className="text-[10px] uppercase tracking-wide text-[var(--cs-ink-muted)]">
        {label}
      </p>
    </div>
  );
}
